import { UpdatePage } from '@components/molecules';
import {
  AutocompleteElement,
  SwitchElement,
  TextFieldElement,
  useForm,
} from 'react-hook-form-mui';
import API from '@apps/nnc/services/productionphase';
import { ModelProps } from '@/types';
import { JSX, useEffect, useCallback } from 'react';
import { Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ProductionPhaseSchema,
  ProductionPhaseSchemaType,
} from '../../schemas/productionphase';
import { LANGUAGES_AVAILABLE } from '@/const';
import { zodResolver } from '@hookform/resolvers/zod';
import React from 'react';
import UnitAPI from '@apps/localization/services/unit';
import AsyncAutocompleteElement from '@/components/atoms/asyncautocomplete';

export default function ObjectUpdatePage(modelProps: ModelProps): JSX.Element {
  const { t } = useTranslation();

  const defaultValues = {
    order: 0,
    is_active: true,
    translations: LANGUAGES_AVAILABLE.map((lang, idx) => ({
      language_code: {
        id: lang.id,
        label: lang.label,
      },
      inlineIndex: idx,
    })),
  };

  const formContext = useForm<ProductionPhaseSchemaType>({
    resolver: zodResolver(ProductionPhaseSchema),
    defaultValues: defaultValues,
  });

  const nextOrder = useCallback(async () => {
    try {
      const response = await API.getNextOrder();
      formContext.setValue('order', response.data.next_order || 0);
    } catch (error) {
      console.error('Error fetching next order:', error);
      return null;
    }
  }, [formContext]);

  const getUnitDivisions = async () => {
    try {
      const unitId = formContext.getValues('unit')?.id;
      const response = await UnitAPI.getUnitDivisions(unitId);
      return response;
    } catch (error) {
      console.error('Error fetching unit divisions:', error);
      return [];
    }
  };

  useEffect(() => {
    nextOrder();
  }, [nextOrder]);

  return (
    <UpdatePage
      schema={ProductionPhaseSchema}
      api={API}
      modelProps={modelProps}
      title={t('Production Phase')}
      formcontext={formContext}
      canSaveAndContinue
      keepValues={['unit', 'division']}
      onAfterSaveAndContinue={nextOrder}
    >
      <>
        {LANGUAGES_AVAILABLE.map((_item, idx) => (
          <React.Fragment key={idx}>
            <TextFieldElement
              name={`translations.${idx}.inlineIndex`}
              value={idx}
              type="hidden"
              sx={{ display: 'none' }}
            />

            <Grid size={{ xs: 12, md: 6 }}>
              <AutocompleteElement
                name={`translations.${idx}.language_code`}
                label={t('Language')}
                options={LANGUAGES_AVAILABLE}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextFieldElement
                name={`translations.${idx}.name`}
                label={t('Name')}
                fullWidth
                required
              />
            </Grid>
          </React.Fragment>
        ))}
        <Grid size={{ xs: 12, md: 6 }}>
          <TextFieldElement
            name="order"
            label={t('Order')}
            fullWidth
            type="number"
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <AsyncAutocompleteElement
            name="unit"
            label={t('Unit')}
            api={() =>
              UnitAPI.select({
                is_active: 1,
              })
            }
            required
            autocompleteProps={{
              onChange: (_, __, reason) => {
                if (reason === 'selectOption' || reason === 'clear') {
                  formContext.setValue('division', undefined);
                }
              },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 10 }}>
          <AsyncAutocompleteElement
            api={getUnitDivisions}
            name="division"
            label={t('Identified Area')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 2 }}>
          <SwitchElement label={t('Is Active?')} name="is_active" />
        </Grid>
      </>
    </UpdatePage>
  );
}
